import Keycloak from 'keycloak-js'
import type { KeycloakTokenParsed } from 'keycloak-js'

export const AUTH_MODE = (import.meta.env.VITE_AUTH_MODE || 'legacy') as
  | 'legacy'
  | 'keycloak'

interface KeycloakConfig {
  url: string
  realm: string
  clientId: string
  silentCheckSsoPath: string
}

interface AuthResult {
  success: boolean
  authenticated: boolean
  error?: Error
}

export class KeycloakAuthService {
  private static instance: KeycloakAuthService | null = null
  private keycloak: Keycloak | null = null
  private config: KeycloakConfig
  private initialized = false
  private readonly isKeycloakMode: boolean

  private constructor(config?: Partial<KeycloakConfig>) {
    this.isKeycloakMode = AUTH_MODE === 'keycloak'
    this.config = {
      url: import.meta.env.VITE_KC_URL,
      realm: import.meta.env.VITE_KC_REALM,
      clientId: import.meta.env.VITE_KC_CLIENT_ID,
      silentCheckSsoPath: '/silent-check-sso.html',
      ...config,
    }

    if (this.isKeycloakMode) {
      this.initializeKeycloak()
    }
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(
    config?: Partial<KeycloakConfig>
  ): KeycloakAuthService {
    if (!KeycloakAuthService.instance) {
      KeycloakAuthService.instance = new KeycloakAuthService(config)
    }
    return KeycloakAuthService.instance
  }

  /**
   * Initialize Keycloak instance
   */
  private initializeKeycloak(): void {
    this.keycloak = new Keycloak({
      url: this.config.url,
      realm: this.config.realm,
      clientId: this.config.clientId,
    })
  }

  /**
   * Initialize Keycloak authentication if needed
   */
  async initializeAuth(): Promise<AuthResult> {
    if (!this.isKeycloakMode) {
      return { success: true, authenticated: false }
    }

    if (!this.keycloak) {
      return {
        success: false,
        authenticated: false,
        error: new Error('Keycloak instance not available'),
      }
    }

    if (this.initialized) {
      return {
        success: true,
        authenticated: this.keycloak.authenticated || false,
      }
    }

    try {
      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        silentCheckSsoRedirectUri: `${window.location.origin}${this.config.silentCheckSsoPath}`,
      })

      this.initialized = true

      return { success: true, authenticated }
    } catch (error) {
      const authError =
        error instanceof Error
          ? error
          : new Error('Unknown authentication error')
      return { success: false, authenticated: false, error: authError }
    }
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    if (!this.isKeycloakMode || !this.keycloak) {
      return null
    }

    if (!this.keycloak.token || this.keycloak.isTokenExpired()) {
      return null
    }

    return this.keycloak.token
  }

  /**
   * Get parsed token data
   */
  getTokenParsed(): KeycloakTokenParsed | null {
    return this.keycloak?.tokenParsed || null
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: string): boolean {
    if (!this.isKeycloakMode) {
      return false
    }

    const ACE_RESOURCE_ACCESS_KEY =
      import.meta.env.VITE_KC_TOKEN_RESOURCE_ACCESS_KEY || 'ace-api'

    const token = this.getTokenParsed()
    if (!token?.resource_access?.[ACE_RESOURCE_ACCESS_KEY]?.roles) {
      return false
    }

    return token.resource_access[ACE_RESOURCE_ACCESS_KEY].roles.includes(role)
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.keycloak?.authenticated || false
  }

  /**
   * Ensure token is fresh with minimum validity
   */
  async ensureFreshToken(minValiditySeconds = 30): Promise<boolean> {
    if (!this.isKeycloakMode || !this.keycloak) {
      return true
    }

    try {
      const refreshed = await this.keycloak.updateToken(minValiditySeconds)
      return refreshed
    } catch (error) {
      this.login()
      return false
    }
  }

  /**
   * Force token refresh or redirect to login
   */
  async forceTokenRefreshOrLogin(minValiditySeconds = 30): Promise<void> {
    const refreshed = await this.ensureFreshToken(minValiditySeconds)

    if (!refreshed && this.keycloak) {
      this.login()
    }
  }

  /**
   * Initiate login process
   */
  login(redirectUri?: string): void {
    if (this.isKeycloakMode && this.keycloak) {
      this.keycloak.login({
        redirectUri: redirectUri || window.location.href,
      })
    }
  }

  /**
   * Initiate logout process
   */
  logout(redirectUri?: string): void {
    if (this.isKeycloakMode && this.keycloak) {
      this.keycloak.logout({
        redirectUri: redirectUri || window.location.origin,
      })
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.keycloak = null
    this.initialized = false
  }
}

// Export singleton instance getter for convenience
export const keycloakAuthService = () => KeycloakAuthService.getInstance()
